<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  provide,
  readonly,
  ref,
  useTemplateRef,
  watch,
} from 'vue';
import {
  type NxSplitterPanelState,
  type NxSplitterSizedPanel,
  type PanelData,
  splitterInjectionKey,
} from './internal';

type PanelState = NxSplitterPanelState;

const {
  initialPanelState = 'split',
  orientation = 'horizontal',
  initialFirstSize,
  sizedPanel = 'first',
} = defineProps<{
  /** The initial panel state. Only evaluated on setup. Defaults to `'split'` */
  initialPanelState?: PanelState;
  /**
   * Divider orientation. `'horizontal'` = horizontal divider, panes stacked
   * (default). `'vertical'` = vertical divider, panes side by side. Setup-only.
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Initial main-axis size (px) of the sized panel (the first by default; see
   * `sizedPanel`). Setup-only; a drag or `v-model:size` overrides it.
   */
  initialFirstSize?: number;
  /**
   * Which panel keeps an explicit px size while the other one flexes.
   * `'first'` (default) is the classic split; `'second'` keeps a right-docked
   * (or bottom-docked) pane at its width when the group resizes. Setup-only.
   */
  sizedPanel?: NxSplitterSizedPanel;
}>();

/**
 * Opt-in `v-model:size`: the main-axis size (px) of the sized panel. External
 * changes are applied (clamped into the panel limits); `update:size` fires when
 * a drag ends and after each keyboard step — never on every pointer move.
 */
const sizeModel = defineModel<number | undefined>('size', { default: undefined });

/**
 * Opt-in `v-model:panel-state`: `'split'`, `'only_first'` or `'only_second'`.
 * Follows the collapse buttons, the keyboard and a collapsing drag; an external
 * change collapses or reopens a panel. Without it `initialPanelState` applies.
 */
const panelStateModel = defineModel<PanelState | undefined>('panelState', {
  default: undefined,
});

/** True when the divider is a vertical line (panes side by side). */
const isVertical = orientation === 'vertical';
/** Index of the panel that carries the explicit size. */
const sizedIndex = sizedPanel === 'second' ? 1 : 0;
/** +1 when moving the divider forward grows the sized panel, -1 when it shrinks it. */
const sizedSign = sizedIndex === 0 ? 1 : -1;
/** The state in which the sized panel is collapsed, and the one in which the other is. */
const sizedCollapsed: PanelState = sizedIndex === 0 ? 'only_second' : 'only_first';
const otherCollapsed: PanelState = sizedIndex === 0 ? 'only_first' : 'only_second';

/** A panel dragged (or keyed) below this size snaps shut, unless a limit holds it open. */
const COLLAPSE_BELOW_PX = 32;

// main-axis coordinate of a pointer event and main-axis size of elements. The size is the
// border box — what the `min-/max-width` (height) a panel is sized by sets under the kit's
// `box-sizing: border-box`. The client box leaves out a panel's scrollbar (`overflow: auto`),
// and a drag started from it jumped by the scrollbar's width on its first move.
const pointerPos = (e: MouseEvent) => (isVertical ? e.clientX : e.clientY);
const elementSize = (el: HTMLElement) => (isVertical ? el.offsetWidth : el.offsetHeight);

const panelState = ref<PanelState>(panelStateModel.value ?? initialPanelState);
const setPanelState = (newPanelState: PanelState) => {
  const oldPanelState = panelState.value;
  if (oldPanelState === newPanelState) return;

  panelState.value = newPanelState;
  updatePanels();
};

const panels: PanelData[] = [];
let calculatedHeight: number | undefined = undefined;
// heightOverwrite is the sized panel's main-axis size (px); name kept for API compatibility
let heightOverwrite: number | undefined = undefined;
/**
 * The size asked for through `v-model:size` (external or initial). The limits
 * clamp from it, not from the last clamped value, so a pane squeezed by a
 * small window grows back to it when the window grows. Unset without the model.
 */
let desiredSize: number | undefined = undefined;
let splitterGroupHeight: number | undefined = undefined;
// Reactive twin of `splitterGroupHeight`, for the separator's ARIA values.
const groupSize = ref<number>();
// Assume the splitter resize handle is 38 pixel height (we receive the actual height on mount via the setResizeHandleHeight function)
let resizeHandleHeight = 38;
let dragStartY: number | undefined = undefined;
let dragStartHeightOverwrite: number | undefined = undefined;

// Body cursor class names follow the divider orientation (not the old inverted axis naming).
const draggingBodyClass = isVertical ? 'dragging-splitter-vertical' : 'dragging-splitter';

const updatePanels = (oldPanelState?: PanelState) => {
  const newPanelState = panelState.value;
  if (oldPanelState === newPanelState) return;

  if (panels.length < 2) return;
  const sized = panels[sizedIndex];
  switch (newPanelState) {
    case 'only_first':
      panels[0].isShown.value = true;
      sized.heightOverwrite.value = undefined;
      panels[1].isShown.value = false;
      showButtonDown.value = false;
      showButtonUp.value = true;
      break;
    case 'split':
      panels[0].isShown.value = true;
      sized.heightOverwrite.value = heightOverwrite;
      panels[1].isShown.value = true;
      showButtonDown.value = true;
      showButtonUp.value = true;
      // The limits are not enforced while a panel is collapsed, so the group may
      // have resized since the size was stored: bring it back into the range.
      clampHeightOverwrite();
      break;
    case 'only_second':
      panels[0].isShown.value = false;
      sized.heightOverwrite.value = undefined;
      panels[1].isShown.value = true;
      showButtonDown.value = true;
      showButtonUp.value = false;
      break;
  }
};

/* ── Size limits (NxSplitterPanel min-size / max-size) ─────────────────── */

/** Without a declared limit the group behaves exactly as it always did. */
const hasLimits = () => panels.some((p) => p.minSize?.() != null || p.maxSize?.() != null);

/**
 * The range the SIZED panel's size may take (the first by default). Only that
 * panel is sized explicitly — the other takes what is left — so the other
 * panel's limits bound it through the space the two share.
 */
function sizedPanelRange(): { lo: number; hi: number } {
  const sized = panels[sizedIndex];
  const other = panels[1 - sizedIndex];
  let lo = sized?.minSize?.() ?? 0;
  let hi = sized?.maxSize?.() ?? Infinity;
  if (splitterGroupHeight !== undefined) {
    const shared = splitterGroupHeight - resizeHandleHeight;
    const otherMin = other?.minSize?.();
    const otherMax = other?.maxSize?.();
    if (otherMax != null) lo = Math.max(lo, shared - otherMax);
    if (otherMin != null) hi = Math.min(hi, shared - otherMin);
  }
  return { lo, hi };
}

/** Clamp into the range. A group too small for both floors keeps the sized panel's. */
function clampSizedPanel(size: number): number {
  const { lo, hi } = sizedPanelRange();
  return Math.max(lo, Math.min(hi, size));
}

/**
 * Before any drag the split is the layout's (both panels flex). Only a limit can
 * make it wrong — a detail pane capped at 620 px in a tall window — and then the
 * first panel is pinned to the nearest size inside the range.
 *
 * While a drag is in progress the size being dragged is the one to keep inside
 * the range: `desiredSize` still holds the size from before the drag (it is only
 * replaced when the drag ends), and clamping from it would snap the panel back
 * to it on every group resize.
 */
function enforceLimits() {
  if (panels.length < 2 || panelState.value !== 'split' || splitterGroupHeight === undefined)
    return;
  if (!hasLimits()) return;
  const fallback = (splitterGroupHeight - resizeHandleHeight) / 2;
  const current =
    dragStartY !== undefined
      ? (heightOverwrite ?? desiredSize ?? fallback)
      : (desiredSize ?? heightOverwrite ?? fallback);
  const clamped = clampSizedPanel(current);
  if (clamped === heightOverwrite || (clamped === current && heightOverwrite === undefined)) return;
  heightOverwrite = clamped;
  panels[sizedIndex].heightOverwrite.value = clamped;
}

const updateHeightOverwrite = () => {
  if (calculatedHeight === undefined) {
    enforceLimits();
    return;
  }

  // Limits first, the collapse snap second: a panel whose floor lies above the
  // snap distance is never snapped shut by a drag, and one capped by the other
  // panel's maximum never makes that panel take the whole group.
  const size = hasLimits() ? clampSizedPanel(calculatedHeight) : calculatedHeight;

  if (size < COLLAPSE_BELOW_PX) {
    heightOverwrite = undefined;
    setPanelState(sizedCollapsed);
  } else if (size > splitterGroupHeight! - resizeHandleHeight - COLLAPSE_BELOW_PX) {
    heightOverwrite = undefined;
    setPanelState(otherCollapsed);
  } else {
    heightOverwrite = size;
    panels[sizedIndex].heightOverwrite.value = heightOverwrite;
    setPanelState('split');
  }
};

/**
 * A group resize (viewport shrink, sidebar toggle, window restore) is not a
 * user gesture: it never changes the panel state. With declared limits it
 * keeps the sized panel inside them (enforceLimits); without, it only keeps a
 * dragged sized-panel size inside the new bounds so the other panel keeps at
 * least COLLAPSE_BELOW_PX. Collapsing here used to unmount a panel's content
 * on a simple window resize and lose whatever was typed in it.
 */
const clampHeightOverwrite = () => {
  if (hasLimits()) {
    enforceLimits();
    return;
  }
  if (heightOverwrite == null || splitterGroupHeight == null) return;
  const max = splitterGroupHeight - resizeHandleHeight - COLLAPSE_BELOW_PX;
  if (heightOverwrite <= max) return;
  heightOverwrite = max >= COLLAPSE_BELOW_PX ? max : undefined;
  if (panelState.value === 'split') panels[sizedIndex].heightOverwrite.value = heightOverwrite;
};

/** Reports the sized panel's size through `v-model:size` (after a drag or a key). */
function emitSize() {
  if (panelState.value !== 'split' || heightOverwrite === undefined) return;
  const rounded = Math.round(heightOverwrite);
  // The user's size is the one asked for now; the echo of the model is no change.
  desiredSize = rounded;
  if (sizeModel.value !== rounded) sizeModel.value = rounded;
}

/**
 * An external size (`v-model:size`) replaces the dragged one: clamped into the
 * limits, applied when split, and remembered for a later reopen.
 */
function applyExternalSize(size: number | undefined) {
  if (size == null || !Number.isFinite(size) || size === desiredSize) return;
  calculatedHeight = undefined;
  desiredSize = size;
  heightOverwrite = hasLimits() ? clampSizedPanel(size) : size;
  if (panels.length === 2 && panelState.value === 'split') {
    panels[sizedIndex].heightOverwrite.value = heightOverwrite;
  }
}

const splitterGroupResizeObserver = new ResizeObserver((observerEntries) => {
  for (const observerEntry of observerEntries) {
    const size = isVertical ? observerEntry.contentRect.width : observerEntry.contentRect.height;
    // Only a change along the divider's own axis moves the limits. The other axis
    // changes whenever the panels' content reflows (cards re-wrapping while the
    // pane is dragged) and must not re-run the clamp.
    if (size === splitterGroupHeight) continue;
    splitterGroupHeight = size;
    groupSize.value = size;
    clampHeightOverwrite();
  }
});
const draggedSize = (payload: MouseEvent) =>
  Math.max(dragStartHeightOverwrite! + sizedSign * (pointerPos(payload) - dragStartY!), 0);
const handlePointerMove = (payload: MouseEvent) => {
  calculatedHeight = draggedSize(payload);
  updateHeightOverwrite();
};
const handlePointerUp = (payload: MouseEvent) => {
  calculatedHeight = draggedSize(payload);
  updateHeightOverwrite();
  emitSize();

  dragStartHeightOverwrite = undefined;
  dragStartY = undefined;

  // Remove dragging event handlers and dragging state from body
  window.removeEventListener('mouseleave', handlePointerMove);
  window.removeEventListener('mousemove', handlePointerMove);
  document.body.classList.remove(draggingBodyClass);
};

const addPanel = (panelData: PanelData) => {
  panels.push(panelData);

  // Wait for both panels to be added
  if (panels.length === 2) {
    const initialSize = sizeModel.value ?? initialFirstSize;
    desiredSize = sizeModel.value ?? undefined;
    if (initialSize != null) {
      heightOverwrite = initialSize;
      panels[sizedIndex].heightOverwrite.value = initialSize;
    }
    updatePanels();
  }
};
const startDragging = (payload: MouseEvent) => {
  dragStartY = pointerPos(payload);
  const sizedElement = panels[sizedIndex]?.panelElement.value;
  dragStartHeightOverwrite = sizedElement ? elementSize(sizedElement) : undefined;

  // Add dragging event handlers and dragging state from body
  window.addEventListener('mouseleave', handlePointerMove, { passive: true });
  window.addEventListener('mousemove', handlePointerMove, { passive: true });
  window.addEventListener('mouseup', handlePointerUp, { once: true });
  document.body.classList.add(draggingBodyClass);
};
const showButtonDown = ref(true);
const clickButtonDown = () => {
  switch (panelState.value) {
    case 'split':
      panelState.value = 'only_first';
      updatePanels();
      break;
    case 'only_second':
      panelState.value = 'split';
      updatePanels();
      break;
  }
};
const showButtonUp = ref(true);
const clickButtonUp = () => {
  switch (panelState.value) {
    case 'split':
      panelState.value = 'only_second';
      updatePanels();
      break;
    case 'only_first':
      panelState.value = 'split';
      updatePanels();
      break;
  }
};
const setResizeHandleHeight = (resizeHandleElementClientHeight: number) => {
  resizeHandleHeight = resizeHandleElementClientHeight;
};

/* ── Keyboard: the divider as a window splitter ────────────────────────── */

/**
 * Arrow keys move the divider through the same path as a drag, so they stop at
 * the same limits and snap shut at the same distance. A collapsed side reopens
 * the way its collapse button reopens it — stepping from zero would stay under
 * the snap distance and never get out. `delta` is the divider's movement
 * (positive = towards the second panel), whichever panel carries the size.
 */
function resizeBy(delta: number) {
  if (panels.length < 2 || delta === 0) return;
  if (panelState.value === 'only_second') {
    if (delta > 0) clickButtonDown();
    return;
  }
  if (panelState.value === 'only_first') {
    if (delta < 0) clickButtonUp();
    return;
  }
  const el = panels[sizedIndex].panelElement.value;
  const current = heightOverwrite ?? (el ? elementSize(el) : 0);
  calculatedHeight = Math.max(current + sizedSign * delta, 0);
  updateHeightOverwrite();
  emitSize();
}

/**
 * Home / End: the sized panel to the end of its range (its `aria-valuenow`
 * minimum or maximum) — a collapse where no limit prevents one.
 */
function resizeToEdge(edge: 'min' | 'max') {
  if (panels.length < 2) return;
  const { lo, hi } = sizedPanelRange();
  const shared = Math.max((splitterGroupHeight ?? 0) - resizeHandleHeight, 0);
  calculatedHeight = edge === 'min' ? lo : Math.min(hi, shared);
  updateHeightOverwrite();
  emitSize();
}

/** `aria-valuenow/min/max`: the sized panel in percent of the space both panels share. */
const separatorValue = computed(() => {
  const size = groupSize.value;
  const state = panelState.value;
  const explicit = panels[sizedIndex]?.heightOverwrite.value;
  const shared = (size ?? 0) - resizeHandleHeight;
  if (panels.length < 2 || shared <= 0) return { now: 50, min: 0, max: 100 };
  const percent = (px: number) => Math.round(Math.min(100, Math.max(0, (px / shared) * 100)));
  const current =
    state === sizedCollapsed ? 0 : state === otherCollapsed ? shared : (explicit ?? shared / 2);
  const { lo, hi } = sizedPanelRange();
  return {
    now: percent(current),
    min: lo >= COLLAPSE_BELOW_PX ? percent(lo) : 0,
    max: hi <= shared - COLLAPSE_BELOW_PX ? percent(hi) : 100,
  };
});

// The panel-state model follows every state change and drives it from outside.
watch(panelState, (state) => {
  if (panelStateModel.value !== state) panelStateModel.value = state;
});
watch(panelStateModel, (state) => {
  if (state && state !== panelState.value) setPanelState(state);
});
watch(sizeModel, (size) => applyExternalSize(size));

provide(splitterInjectionKey, {
  addPanel,
  startDragging,
  showButtonDown: readonly(showButtonDown),
  clickButtonDown,
  showButtonUp: readonly(showButtonUp),
  clickButtonUp,
  setResizeHandleHeight,
  isVertical,
  separator: {
    resizeBy,
    resizeToEdge,
    value: separatorValue,
    controls: () => panels[sizedIndex]?.panelElement.value?.id || undefined,
  },
});

defineExpose({ panelState, setPanelState });

const splitterGroup = useTemplateRef('splitter-group');

onMounted(() => {
  const splitterGroupElement = splitterGroup.value!;
  splitterGroupResizeObserver.observe(splitterGroupElement);
});

onUnmounted(() => {
  // Remove dragging event handlers and dragging state from body
  window.removeEventListener('mouseleave', handlePointerMove);
  window.removeEventListener('mousemove', handlePointerMove);
  window.removeEventListener('mouseup', handlePointerUp);
  document.body.classList.remove(draggingBodyClass);

  splitterGroupResizeObserver.disconnect();
});
</script>

<template>
  <div
    ref="splitter-group"
    class="splitter-group"
    :class="{ 'splitter-group--vertical': isVertical }"
  >
    <slot />
  </div>
</template>

<style scoped>
/* The cursor and line look while a divider is dragged are global rules of
   NxSplitterResizeHandle (a free-standing handle drags without a group). */
.splitter-group {
  display: flex;
  height: 100%;
  flex-direction: column;
}

/* Vertical divider → panes side by side */
.splitter-group--vertical {
  flex-direction: row;
}
</style>
